import React, { useState, useEffect, useRef } from 'react';

// Album art thumbnail that never makes the layout jump:
// - always takes up its space: a placeholder tile shows whenever there's no artwork
//   (none sent, still loading, or the image failed to load)
// - keeps showing the previous artwork until the new one has fully loaded,
//   then cross-fades to it
// - ignores brief gaps (Discord often sends a track change before its artwork):
//   the last artwork stays for a few seconds, then cross-fades to the placeholder
const FADE_MS = 400;
const GRACE_MS = 4000;
const PLACEHOLDER = '__placeholder__';

function Layer({ value, fadeIn }) {
  const style = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    ...(fadeIn && { animation: `albumArtFadeIn ${FADE_MS}ms ease forwards` }),
  };
  if (value === PLACEHOLDER) {
    return (
      <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#18181b' }}>
        <svg width="40%" height="40%" viewBox="0 0 24 24" fill="none" stroke="#52525b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" />
        </svg>
      </div>
    );
  }
  return <img src={value} alt="" referrerPolicy="no-referrer" style={{ ...style, objectFit: 'cover', display: 'block' }} />;
}

export default function AlbumArt({ src, size, style }) {
  const [shown, setShown] = useState(PLACEHOLDER);
  const [prev, setPrev] = useState(null);
  const shownRef = useRef(shown);
  shownRef.current = shown;

  // Cross-fade: the old layer stays underneath while the new one fades in
  const show = (next) => {
    if (next === shownRef.current) return;
    setPrev(shownRef.current);
    setShown(next);
    shownRef.current = next;
  };

  // Drop the old layer once the fade has finished
  useEffect(() => {
    if (!prev) return;
    const timer = setTimeout(() => setPrev(null), FADE_MS + 50);
    return () => clearTimeout(timer);
  }, [prev, shown]);

  useEffect(() => {
    if (!src) {
      // Artwork gone: keep the last one briefly in case it comes straight back, then show the placeholder
      const timer = setTimeout(() => show(PLACEHOLDER), GRACE_MS);
      return () => clearTimeout(timer);
    }
    // Preload, and only swap once the new image is ready
    let cancelled = false;
    const img = new Image();
    img.referrerPolicy = 'no-referrer';
    img.onload = () => { if (!cancelled) show(src); };
    img.onerror = () => { if (!cancelled) show(PLACEHOLDER); };
    img.src = src;
    return () => { cancelled = true; };
  }, [src]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      style={{
        position: 'relative',
        flexShrink: 0,
        width: size,
        height: size,
        borderRadius: '6px',
        overflow: 'hidden',
        border: '1px solid rgba(63,63,70,0.5)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
        background: '#18181b',
        ...style,
      }}
    >
      <style>{`@keyframes albumArtFadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
      {prev && <Layer key={`prev-${prev}`} value={prev} />}
      <Layer key={`shown-${shown}`} value={shown} fadeIn={Boolean(prev)} />
    </div>
  );
}
